import React, {useState, useEffect} from 'react';
import {CURRENT_FOUR_YEAR_NOT_TRANSFERRING} from '../../views/public/utils/utils';
import {UserIdentifier, EducationPhase} from '../../types/enums';
import {useHistory} from 'react-router-dom';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import {useBoolean} from '../../hooks/useBoolean';
import useAsyncEffect from '../../hooks/use-async-effect';
import {NewFeatureInteractionService} from '../../services/FeatureInteractionService';
import useUserType from '../../hooks/use-user-type';
import {useSelector} from 'react-redux';
import {Store} from '../../types/store';
import ParentWelcomeModal from '../../views/parent/WelcomeModal/WelcomeModal';
import VideoModal from '../video-modal/video-modal';
import ChecklistModal from '../checklist-modal/checklist-modal';
import CollegeSearchWelcomeModal from '../CollegeSearch/welcome-modal/welcome-modal';
import ShareDataModal from '../share-data-modal/share-data-modal';
import StudentWelcomeModal from '../student-welcome-modal/student-welcome-modal';
import CantEarnModal from '../cant-earn-modal/cant-earn-modal';

export interface FeatureInteractionModalConfiguration {
  featureInteractionName: string;
  pathPattern: RegExp;
  featureFlag?: string;
  conditions?: () => boolean;
}

type FeatureInteractionModalConfigurationWithUserIdentifiers =
  FeatureInteractionModalConfiguration & {
    userIdentifiers: UserIdentifier[];
  };

const atCurrentFourYearNotTransferring = window.sessionStorage.getItem(
  CURRENT_FOUR_YEAR_NOT_TRANSFERRING
);

const featureInteractionModalConfigurations: FeatureInteractionModalConfigurationWithUserIdentifiers[] =
  [
    {
      featureInteractionName: 'parent-welcome-modal',
      pathPattern: /\/parents\/dashboard(.*)/,
      userIdentifiers: [UserIdentifier.PARENT],
    },
    {
      featureInteractionName: 'video-welcome-modal',
      pathPattern: /\/videos$/,
      userIdentifiers: [UserIdentifier.HS_STUDENT, UserIdentifier.CC_STUDENT],
    },
    {
      featureInteractionName: 'checklist-welcome-modal',
      pathPattern: /\/checklist$/,
      userIdentifiers: [UserIdentifier.HS_STUDENT, UserIdentifier.CC_STUDENT],
    },
    {
      featureInteractionName: 'college-search-welcome-modal',
      pathPattern: /\/colleges\/(recommendations|search|my-list)/,
      userIdentifiers: [UserIdentifier.HS_STUDENT, UserIdentifier.CC_STUDENT],
    },
    {
      featureInteractionName: 'share-data-modal',
      pathPattern: /.*/,
      userIdentifiers: [UserIdentifier.HS_STUDENT, UserIdentifier.CC_STUDENT],
    },
    {
      featureInteractionName: 'student-welcome-modal',
      pathPattern: /\/home$/,
      userIdentifiers: [UserIdentifier.HS_STUDENT, UserIdentifier.CC_STUDENT],
    },
    {
      featureInteractionName: 'cant-earn-modal',
      pathPattern: /\/home$/,
      userIdentifiers: [UserIdentifier.CC_STUDENT],
      conditions: () => !!atCurrentFourYearNotTransferring,
    },
  ];

export const featureInteractionModalConfigurationsByUserIdentifier: Record<
  UserIdentifier,
  FeatureInteractionModalConfiguration[]
> = featureInteractionModalConfigurations.reduce(
  (acc, config) => {
    config.userIdentifiers.forEach((userIdentifier) => {
      if (!acc[userIdentifier]) acc[userIdentifier] = [];
      acc[userIdentifier].push({
        featureInteractionName: config.featureInteractionName,
        pathPattern: config.pathPattern,
        featureFlag: config.featureFlag,
        conditions: config.conditions,
      });
    });
    return acc;
  },
  {} as Record<UserIdentifier, FeatureInteractionModalConfiguration[]>
);

const ChecklistModalWrapper = ({handleClose}: {handleClose: () => void}) => (
  <ChecklistModal handleClose={handleClose} educationPhase={EducationPhase.HIGH_SCHOOL} />
);

const MODAL_COMPONENTS: Record<string, React.ComponentType<{handleClose: () => void}>> = {
  'parent-welcome-modal': ParentWelcomeModal,
  'video-welcome-modal': VideoModal,
  'checklist-welcome-modal': ChecklistModalWrapper,
  'college-search-welcome-modal': CollegeSearchWelcomeModal,
  'share-data-modal': ShareDataModal,
  'student-welcome-modal': StudentWelcomeModal,
  'cant-earn-modal': CantEarnModal,
};

const FeatureInteractionModals = ({
  configurations,
}: {
  configurations: FeatureInteractionModalConfiguration[];
}) => {
  const featureFlags = configurations.map((c) => c.featureFlag).filter(Boolean) as string[];
  const history = useHistory();
  const path = history.location.pathname;
  const flags = useFeatureFlags(featureFlags);
  const featureInteractionNames = configurations.map((c) => c.featureInteractionName);
  const featureInteractionsLoaded = useBoolean(false);
  const [seenModals, setSeenModals] = useState<string[]>([]);
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const flagsLoaded =
    featureFlags.length === 0 || featureFlags.every((f) => flags[f] !== undefined);

  useAsyncEffect(async () => {
    const featureInteractions = await NewFeatureInteractionService.index({
      featureNames: featureInteractionNames.join(','),
    });
    setSeenModals(featureInteractions.map(({featureName}) => featureName));
    featureInteractionsLoaded.setTrue();
  }, []);

  useEffect(() => {
    if (!featureInteractionsLoaded.value) return;
    if (!flagsLoaded) return;
    if (activeModal) return;

    for (const config of configurations) {
      const alreadySeen = seenModals.includes(config.featureInteractionName);
      const pathMatches = config.pathPattern.test(path);
      const flagOk = !config.featureFlag || flags[config.featureFlag];
      const conditionsOk = !config.conditions || config.conditions();

      if (!alreadySeen && pathMatches && flagOk && conditionsOk) {
        setActiveModal(config.featureInteractionName);
        return;
      }
    }
  }, [
    seenModals,
    flags,
    featureInteractionsLoaded.value,
    flagsLoaded,
    configurations,
    path,
    activeModal,
  ]);

  const handleClose = async () => {
    if (!activeModal) return;
    await NewFeatureInteractionService.create({
      featureData: {featureName: activeModal},
    });
    setSeenModals((prev) => [...prev, activeModal]);
    setActiveModal(null);
  };

  if (!activeModal) return null;
  const ModalComponent = MODAL_COMPONENTS[activeModal];
  if (!ModalComponent) return null;
  return <ModalComponent handleClose={handleClose} />;
};

const FeatureInteractionModalsWrapper = () => {
  const {userIdentifier} = useUserType();
  const onboarding = useSelector((state: Store) => state.onboarding);

  if (onboarding) return null;

  let configurations = featureInteractionModalConfigurationsByUserIdentifier[userIdentifier] || [];
  configurations = [
    ...configurations.filter((config) => config.featureInteractionName === 'share-data-modal'),
    ...configurations.filter((config) => config.featureInteractionName !== 'share-data-modal'),
  ];

  if (configurations.length === 0) return null;
  return <FeatureInteractionModals configurations={configurations} />;
};

export default FeatureInteractionModalsWrapper;
